import { FormInput } from "@frontend/components/elements/form/Form.styles";
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useRef, useState } from "react";
import type {
  Control,
  ControllerRenderProps,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

const API_KEY = process.env.NEXT_PUBLIC_GCP_MAPS_API as string;

const PlaceAutocomplete = ({
  register,
  field,
  questionText,
  isRequired,
  getValues,
  setValue,
  trigger,
  updateProgress,
  questionId,
  isAdmin,
  initiateAutosave,
  disabled,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  register: any;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: ControllerRenderProps<Partial<any>, string>;
  questionText: string;
  isRequired: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  getValues: UseFormGetValues<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  setValue: UseFormSetValue<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  trigger: UseFormTrigger<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  updateProgress: (updatedField: string, updatedValue: any) => void;
  questionId: number;
  isAdmin: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
}) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["geometry", "name", "formatted_address", "place_changed"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  placeAutocomplete?.addListener("place_changed", (e: any) => {
    const place = placeAutocomplete.getPlace();
    if (place) {
      setValue(`${field.name}`, place.name);
      trigger(`${field.name}`);
    } else {
      const preLoadedPlace = getValues(`${field.name}`);
      if (preLoadedPlace && inputRef.current) {
        inputRef.current.value = preLoadedPlace;
      }
    }
  });

  useEffect(() => {
    if (placeAutocomplete) {
      google.maps.event.trigger(placeAutocomplete, "place_changed");
    }
  }, [placeAutocomplete]);

  return (
    <FormInput
      {...field}
      onChange={(e) => {
        updateProgress(field.name, e.target.value);
        field.onChange(e);
      }}
      onBlur={(e) => {
        initiateAutosave({ ...getValues(), [field.name]: e.target.value });
      }}
      $labelColor="#000000"
      type="text"
      label={isRequired ? `${questionText}*` : questionText}
      style={{ marginTop: "28px" }}
      key={field.name}
      data-cy={`intake-${questionText}-input`}
      readonly={isAdmin}
      disabled={disabled}
    />
  );
};

export const IntakeAddressInput = ({
  isRequired,
  questionId,
  questionText,
  field,
  register,
  errors,
  getValues,
  setValue,
  trigger,
  control,
  updateProgress,
  initiateAutosave,
  isAdmin,
  disabled,
}: {
  isRequired: boolean;
  questionId: number;
  questionText: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: ControllerRenderProps<Partial<any>, string>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  register: any;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  errors: FieldErrors<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  getValues: UseFormGetValues<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  setValue: UseFormSetValue<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  trigger: UseFormTrigger<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  control: Control<Partial<any>, any>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  updateProgress: (updatedField: string, updatedValue: any) => void;
  isAdmin: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
}) => {
  return (
    <APIProvider
      key={questionId}
      apiKey={API_KEY}
      version="quarterly"
      solutionChannel="GMP_devsite_samples_v3_rgmautocomplete"
    >
      <div className="w-full flex-col gap-1 h-full flex">
        <PlaceAutocomplete
          questionId={questionId}
          register={register}
          field={field}
          questionText={questionText}
          isRequired={isRequired}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          updateProgress={updateProgress}
          isAdmin={isAdmin}
          initiateAutosave={initiateAutosave}
          disabled={disabled}
        />
      </div>
    </APIProvider>
  );
};
